import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Snackbar, Alert, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  Grid, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import config from '../../../config';

// HR Tips — daily-or-event tip entry + per-employee distribution. Unlike
// Service Charge (percentage based) tips are distributed as direct amounts
// since cash splits don't usually round to clean percents.
export default function Tips() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const companyId = company?.id;
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = (() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); })();

  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);

  const [poolDialog, setPoolDialog] = useState(false);
  const [poolForm, setPoolForm] = useState({ tipDate: today, amount: 0, source: '', remarks: '' });

  const [distDialog, setDistDialog] = useState(false);
  const [distTarget, setDistTarget] = useState(null);
  const [distRows, setDistRows] = useState([]); // {employeeId, amount, include}

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const loadEmployees = async () => {
    const res = await fetch(`${config.API_BASE_URL}/Hr/Employees/${companyId}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setEmployees(await res.json());
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fromDate) qs.append('from', fromDate);
      if (toDate) qs.append('to', toDate);
      const res = await fetch(`${config.API_BASE_URL}/Hr/Tips/${companyId}?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setRows(await res.json());
    } catch (e) { toast('Failed to load', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (companyId) loadEmployees(); /* eslint-disable-next-line */ }, [companyId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, fromDate, toDate]);

  const savePool = async () => {
    const body = {
      Id: null, CompanyId: companyId,
      TipDate: poolForm.tipDate,
      Amount: Number(poolForm.amount) || 0,
      Source: poolForm.source || null,
      Remarks: poolForm.remarks || null,
      UserId: user?.id || null,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/Tips`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) { toast('Save failed', 'error'); return; }
    toast('Tip recorded');
    setPoolDialog(false);
    load();
  };

  const openDistribute = async (r) => {
    setDistTarget(r);
    try {
      const res = await fetch(`${config.API_BASE_URL}/Hr/TipsDistribution/${r.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const existing = res.ok ? await res.json() : [];
      const existingMap = new Map(existing.map((e) => [e.employeeId, e]));
      setDistRows(employees.map((e) => ({
        employeeId: e.id,
        employeeName: e.employeeName,
        employeeCode: e.employeeCode,
        amount: existingMap.get(e.id)?.amount || 0,
        include: existingMap.has(e.id),
      })));
    } catch (e) { setDistRows([]); }
    setDistDialog(true);
  };

  const equalSplit = () => {
    const included = distRows.filter((r) => r.include);
    if (included.length === 0 || !distTarget) return;
    const per = Math.round((Number(distTarget.amount) / included.length) * 100) / 100;
    setDistRows((prev) => prev.map((r) => r.include ? { ...r, amount: per } : r));
  };

  const totalDist = distRows.filter((r) => r.include).reduce((a, r) => a + Number(r.amount || 0), 0);
  const overflow = distTarget && totalDist > Number(distTarget.amount) + 0.5;

  const saveDistribution = async () => {
    if (overflow) {
      if (!window.confirm(`Distribution ₹${totalDist} exceeds pool ₹${distTarget.amount}. Save anyway?`)) return;
    }
    const body = {
      CompanyId: companyId,
      TipsId: distTarget.id,
      Rows: distRows.filter((r) => r.include && r.amount > 0).map((r) => ({
        EmployeeId: r.employeeId,
        Amount: Number(r.amount) || 0,
      })),
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/TipsDistribute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) { toast('Save failed', 'error'); return; }
    toast('Distribution saved');
    setDistDialog(false);
    load();
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2, justifyContent: 'space-between' }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: '#a855f7', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <VolunteerActivismIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Tips</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>HR · Daily tips + distribution</Typography>
          </Box>
        </Stack>
        <Button variant='contained' startIcon={<AddIcon />}
          onClick={() => { setPoolForm({ tipDate: today, amount: 0, source: '', remarks: '' }); setPoolDialog(true); }}
          sx={{ bgcolor: '#a855f7', '&:hover': { bgcolor: '#9333ea' } }}>
          New Tip
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField type='date' size='small' label='From' InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} sx={{ bgcolor: '#fff' }} />
        <TextField type='date' size='small' label='To' InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} sx={{ bgcolor: '#fff' }} />
      </Stack>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Distributed</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Pending</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'># Staff</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={8} align='center' sx={{ py: 4 }}><Typography sx={{ color: '#94a3b8' }}>No tips in this range.</Typography></TableCell></TableRow>
                  )}
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{new Date(r.tipDate).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell align='right' sx={{ fontWeight: 700 }}>₹{Number(r.amount).toLocaleString('en-IN')}</TableCell>
                      <TableCell align='right' sx={{ color: '#16a34a' }}>₹{Number(r.distributed).toLocaleString('en-IN')}</TableCell>
                      <TableCell align='right' sx={{ color: r.pending > 0.5 ? '#dc2626' : '#94a3b8' }}>₹{Number(r.pending).toLocaleString('en-IN')}</TableCell>
                      <TableCell align='right'>{r.distributionCount}</TableCell>
                      <TableCell sx={{ color: '#475569' }}>{r.source || '—'}</TableCell>
                      <TableCell sx={{ color: '#475569' }}>{r.remarks || '—'}</TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Distribute'><IconButton size='small' onClick={() => openDistribute(r)}><VisibilityIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={poolDialog} onClose={() => setPoolDialog(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Tip</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}><TextField type='date' size='small' fullWidth label='Date *' InputLabelProps={{ shrink: true }} value={poolForm.tipDate} onChange={(e) => setPoolForm({ ...poolForm, tipDate: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField type='number' size='small' fullWidth label='Amount *' value={poolForm.amount} onChange={(e) => setPoolForm({ ...poolForm, amount: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField size='small' fullWidth label='Source (table/room/guest)' value={poolForm.source} onChange={(e) => setPoolForm({ ...poolForm, source: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField size='small' fullWidth multiline rows={2} label='Remarks' value={poolForm.remarks} onChange={(e) => setPoolForm({ ...poolForm, remarks: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPoolDialog(false)}>Cancel</Button>
          <Button variant='contained' onClick={savePool}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={distDialog} onClose={() => setDistDialog(false)} maxWidth='md' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Distribute Tip
          {distTarget && (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>
              {new Date(distTarget.tipDate).toLocaleDateString('en-IN')} · Pool ₹{Number(distTarget.amount).toLocaleString('en-IN')}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction='row' spacing={1.5} sx={{ mb: 1.5, alignItems: 'center' }}>
            <Button size='small' variant='outlined' onClick={equalSplit}>Equal Split (included)</Button>
            <Box sx={{ flex: 1 }} />
            <Chip label={`Distributed: ₹${totalDist.toLocaleString('en-IN')} / ₹${Number(distTarget?.amount || 0).toLocaleString('en-IN')}`}
              sx={{ bgcolor: overflow ? '#fee2e2' : '#dcfce7', color: overflow ? '#991b1b' : '#166534', fontWeight: 700 }} />
          </Stack>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox'>
                    <Checkbox size='small'
                      checked={distRows.length > 0 && distRows.every((r) => r.include)}
                      indeterminate={distRows.some((r) => r.include) && !distRows.every((r) => r.include)}
                      onChange={(e) => setDistRows((prev) => prev.map((r) => ({ ...r, include: e.target.checked })))} />
                  </TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell align='right'>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {distRows.map((r, idx) => (
                  <TableRow key={r.employeeId}>
                    <TableCell padding='checkbox'>
                      <Checkbox size='small' checked={r.include}
                        onChange={(e) => setDistRows((prev) => { const next = [...prev]; next[idx] = { ...next[idx], include: e.target.checked }; return next; })} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {r.employeeName}
                      <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{r.employeeCode}</Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <TextField type='number' size='small' value={r.amount} disabled={!r.include}
                        onChange={(e) => setDistRows((prev) => { const next = [...prev]; next[idx] = { ...next[idx], amount: e.target.value }; return next; })}
                        inputProps={{ min: 0, step: 0.01 }} sx={{ width: 120 }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDistDialog(false)}>Cancel</Button>
          <Button variant='contained' onClick={saveDistribution} sx={{ bgcolor: '#a855f7', '&:hover': { bgcolor: '#9333ea' } }}>Save Distribution</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
